// src/portal-app/shell/kit/SearchInput.js
//
// Glass-backed search field. Cross-cutting by definition (spec §6) — moved
// here from operations/kit.js (Plan 4 Task 1). Class names (.opk-*) kept
// byte-identical; rules moved into kitCss.js.
//
// Leading search icon, trailing clear button (visible when non-empty).
// Debounce: onChange is called after `debounce` ms of quiet.
// >=44pt height. Controlled: value + onChange from parent.
import React, { useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { ensureKitCss } from './kitCss';

const ICON = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

const SearchIcon = () => (
    <svg {...ICON} width="16" height="16">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
    </svg>
);

const CloseIcon = () => (
    <svg {...ICON} width="14" height="14">
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
    </svg>
);

// props:
//   value       string
//   onChange    (value: string) => void
//   placeholder string
//   debounce    number  ms (default 300, 0 = no debounce)
export function SearchInput({ value, onChange, placeholder, debounce: debounceMs = 300 }) {
    ensureKitCss();
    const { t } = useLanguage();
    const timerRef = useRef(null);

    const handleInput = useCallback((e) => {
        const v = e.target.value;
        if (debounceMs <= 0) {
            onChange(v);
            return;
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            onChange(v);
            timerRef.current = null;
        }, debounceMs);
    }, [onChange, debounceMs]);

    // Cleanup on unmount
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const handleClear = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        onChange('');
    };

    return (
        <div className="opk-search">
            <span className="opk-search-icon" data-testid="search-icon" aria-hidden="true">
                <SearchIcon />
            </span>
            <input
                type="search"
                className="opk-search-input"
                value={value}
                placeholder={placeholder || t('search', {}, 'Search')}
                onInput={handleInput}
                onChange={handleInput}
                aria-label={placeholder || t('search', {}, 'Search')}
            />
            {value ? (
                <button
                    type="button"
                    className="opk-search-clear"
                    data-testid="search-clear"
                    aria-label={t('clear_search', {}, 'Clear search')}
                    onClick={handleClear}
                >
                    <CloseIcon />
                </button>
            ) : null}
        </div>
    );
}

export default SearchInput;
